import React from 'react';
import { analyticsEvents } from '../utils/analytics';
import { hapticFeedback } from '../utils/haptic';

export type QuestionType = 'comparison';

export interface Question {
  id: string;
  type: QuestionType;
  text: string;
  options?: string[];
}

interface QuestionFlowProps {
  currentQuestion: Question;
  onAnswer: (answer: string | number) => void;
  isWaitingForResponse: boolean;
}

export const QuestionFlow: React.FC<QuestionFlowProps> = ({
  currentQuestion,
  onAnswer,
  isWaitingForResponse,
}) => {
  const handleComparisonAnswer = (answer: string) => {
    if (!isWaitingForResponse) return;
    
    // Haptic feedback for answer
    hapticFeedback.answerQuestion();
    
    // Track user answer
    analyticsEvents.trackFeatureInteraction('question_flow', 'answer_selected', {
      answer: answer,
      question_id: currentQuestion.id,
      question_text: currentQuestion.text
    });
    
    onAnswer(answer);
  };

  const renderComparisonQuestion = () => (
    <div className="space-y-6">
      <p className="text-lg text-gray-700 text-center mb-6" aria-describedby="question-heading">{currentQuestion.text}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" role="group" aria-labelledby="question-heading">
        <button
          onClick={() => handleComparisonAnswer('lower')}
          disabled={!isWaitingForResponse}
          className="medical-button px-6 py-4 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 text-lg"
          aria-label="My tinnitus is lower than this sound"
        >
          Lower
        </button>
        <button
          onClick={() => handleComparisonAnswer('match')}
          disabled={!isWaitingForResponse}
          className="medical-button px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 text-lg"
          aria-label="This sound matches my tinnitus frequency"
        >
          Exact Match!
        </button>
        <button
          onClick={() => handleComparisonAnswer('higher')}
          disabled={!isWaitingForResponse}
          className="medical-button px-6 py-4 bg-purple-700 hover:bg-purple-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 text-lg"
          aria-label="My tinnitus is higher than this sound"
        >
          Higher
        </button>
      </div>
    </div>
  );


  return (
    <div className="bg-white rounded-lg p-6 shadow-medical border border-medical-gray-200" role="main" aria-live="polite">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-purple-700" id="question-heading">
          Finding Your Frequency - Step {currentQuestion.id}
        </h2>
      </div>
      
      {renderComparisonQuestion()}
      
      {!isWaitingForResponse && (
        <p className="text-center text-yellow-600 mt-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
          Click 'Play Sound' to hear the test frequency
        </p>
      )}
    </div>
  );
};